import React from "react";
import { Col, Row, Spinner } from "reactstrap";
import { getFile } from "../../utils/digitalOcean/digitalocean-upload-file";
import Loader from "./Loader";

/**
 * HO-TASK-247: "Datos del inmueble" y "Datos adicionales", cada uno en su
 * propia tarjeta y con la etiqueta y el valor en columnas separadas, como en el
 * PDF de la planilla.
 *
 * Antes los dos bloques iban dentro de una sola tarjeta y cada dato se escribía
 * como "Etiqueta: valor" en una misma línea, así que los valores quedaban
 * desalineados y los dos bloques se leían como uno solo. Lo usan todas las
 * pantallas que muestran una planilla -- las del administrador y las del
 * propietario -- así que el cambio vale para todas por igual.
 */
const InfoPanel = ({ title, rows = [] }) => (
  // h-100: los dos paneles quedan del mismo alto aunque uno tenga más filas
  // que el otro. Sin esto el de menos datos terminaba más arriba y se veían
  // desparejos.
  // El margen de separación va en la columna que lo envuelve, no acá: con
  // h-100 el panel mide lo mismo que su columna y un margen propio se le sumaba
  // por fuera, así que el bloque siguiente quedaba pegado igual.
  <div
    className="h-100"
    style={{
      border: "1px solid #d8e2ef",
      borderRadius: "6px",
      overflow: "hidden",
      // La tabla crece hasta el borde de abajo: con h-100 a secas, el panel
      // que tiene filas más bajas se estiraba igual y quedaba un hueco en
      // blanco después de la última fila.
      display: "flex",
      flexDirection: "column",
    }}
  >
    {/* Tipografía del tema (fs-0 / fs--1) en vez de tamaños propios, para que
    estos paneles se lean igual que las tablas que van debajo. */}
    <div
      className="fs-0 font-weight-bold text-center"
      style={{
        backgroundColor: "#f9fafd",
        borderBottom: "1px solid #d8e2ef",
        padding: "8px",
      }}
    >
      {title}
    </div>
    <table style={{ width: "100%", borderCollapse: "collapse", flex: 1 }}>
      <tbody>
        {rows.map(({ label, value }, index) => (
          <tr
            key={index}
            style={{ backgroundColor: index % 2 === 0 ? "#f9fafd" : "#ffffff" }}
          >
            <td
              className="fs--1 font-weight-semi-bold"
              style={{ width: "45%", padding: "6px 10px" }}
            >
              {label}
            </td>
            <td className="fs--1" style={{ padding: "6px 10px" }}>
              {value}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const ItemImageTitleSubProperty = ({
  loading,
  image,
  titleCondominium,
  titleProperty,
  titleInvoice,
  title1,
  title2,
  condominium = [],
  property = [],
  invoice = [],
  subtitles = [],
  noDivider = false,
}) => {
  const imageInterprete = (file) => {
    let image;
    if (typeof file === "string") {
      image = String(file).includes("http") ? (image = file) : getFile(file);
    } else {
      image = URL.createObjectURL(file);
    }
    return image;
  };

  if (loading) {
    return (
      <Row className="p-4 align-items-center justify-content-center text-center mb-3">
      {/*   <Spinner /> */}
        <Loader padding="py-5" />
      </Row>
    );
  }

  return (
    <>
      <Row className="pl-3 align-items-center text-center mb-3">
        {image && (
          <Col
            sm={12}
            md={"auto"}
            className="d-flex p-0 justify-content-center align-items-center"
          >
            {/* HO-TASK-254: el logo va como <img>, no como fondo CSS. Los
                navegadores no imprimen fondos por defecto, así que al
                imprimir la planilla quedaba un cuadro vacío de 150px arriba
                del título. Una imagen sí se imprime. */}
            <div
              className="overflow-hidden d-flex justify-content-center align-items-center"
              style={{
                width: "150px",
                height: "150px",
                borderRadius: "10%",
                background: "rgba(0, 0, 0, 0.13)",
                printColorAdjust: "exact",
                WebkitPrintColorAdjust: "exact",
              }}
            >
              <img
                src={imageInterprete(image)}
                alt=""
                style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
              />
              {loading && <Spinner color="falcon-default" />}
            </div>
          </Col>
        )}
        <Col className="col text-md-left mt-3 mt-md-0">
          {Boolean(title1) && (
            <div className="row pl-0 pl-md-3 align-items-center justify-content-center justify-content-md-start">
              <div className="col-auto pl-sm-0 pl-md-2 pr-0  col-xs-12">
                <h4>{title1}</h4>
              </div>
            </div>
          )}
          {Boolean(titleCondominium) && (
            <div className="row pl-0 pl-md-3 align-items-center justify-content-center justify-content-md-start">
              <div className="col-auto px-0">
                <h4 style={{ marginLeft: "7px" }}>{titleCondominium}</h4>
              </div>
            </div>
          )}
          {Boolean(condominium?.length) &&
            condominium.map(({ label, value }, i) => (
              <p key={i} className="fs--1 pl-2 mb-0">
                <strong>{label}: </strong>
                {value}
              </p>
            ))}
        </Col>
        {!noDivider && (
          <Col xs={12}>
            {/* my-2: la línea traía 1rem arriba y abajo y dejaba un hueco
            desproporcionado entre la cabecera y las tarjetas de datos. */}
            <hr className="my-2" />
          </Col>
        )}
      </Row>
      {titleProperty || titleInvoice ? (
        <Row>
          {Boolean(titleProperty) && (
            <Col md={titleInvoice ? 6 : 12} className="mb-3">
              <InfoPanel title={titleProperty} rows={property} />
            </Col>
          )}
          {Boolean(titleInvoice) && (
            <Col md={titleProperty ? 6 : 12} className="mb-3">
              <InfoPanel title={titleInvoice} rows={invoice} />
            </Col>
          )}
        </Row>
      ) : null}
    </>
  );
};

export default ItemImageTitleSubProperty;
